import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  TextField,
  useTheme,
} from '@mui/material';
import {
  AutoMode,
  Build,
  Email,
  HandshakeOutlined,
  Sms,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  Task,
  TaskAutomationLevel,
  TaskChannel,
  TaskEvent,
  TASK_EVENT_LABELS,
} from '@otw/models/tasks';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { TaskCustomerLeadSummary, TaskCustomerOption } from '../../util/taskUtils';

export type TaskFormSubmitPayload = Omit<
  Task,
  'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'completedAt' | 'status'
>;

export interface TaskAssignableUser {
  id: string;
  displayName: string;
  email?: string | null;
}

interface TaskFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (task: TaskFormSubmitPayload) => Promise<void>;
  customers: TaskCustomerOption[];
  defaultCustomerId?: string;
  isSubmitting?: boolean;
  /**
   * If provided, the dialog opens in edit mode and prefills with the task values.
   */
  initialTask?: Task | null;
  /**
   * When provided (typically for users with full lead-management access), the
   * form exposes an "Assigned to" picker so the task can be created on behalf
   * of any of these reps. Omit (or pass undefined) to keep the task assigned
   * to the current user, matching the legacy behavior.
   */
  assignableUsers?: TaskAssignableUser[];
}

const EVENT_OPTIONS: TaskEvent[] = [
  'follow_up',
  'birthday',
  'truck_anniversary',
  'lead_lost',
];

const formatLeadOption = (lead: TaskCustomerLeadSummary): string => {
  const datePart = lead.createdAt
    ? dayjs(lead.createdAt).format('MMM D, YYYY')
    : 'Unknown date';
  const companyPart = lead.company || 'No company';
  return `${datePart} · ${companyPart}`;
};

interface AutomationOption {
  value: TaskAutomationLevel;
  label: string;
  description: string;
  icon: React.ReactNode;
  /** MUI palette key used for the option's accent color. */
  accent: 'primary' | 'success' | 'warning';
}

const AUTOMATION_OPTIONS: AutomationOption[] = [
  {
    value: 'full',
    label: 'Full',
    description:
      'Auto-complete at 9:00 AM on the due date. The selected channels will be sent automatically using the notes you provide as the personalized message.',
    icon: <AutoMode />,
    accent: 'success',
  },
  {
    value: 'partial',
    label: 'Partial',
    description:
      'You complete the task yourself. We open the compose dialog so you can write a personal message and send the email and/or text.',
    icon: <HandshakeOutlined />,
    accent: 'primary',
  },
  {
    value: 'manual',
    label: 'Manual',
    description:
      'For tasks that can\'t be done with a single email/text. The complete button just marks the task done — no message dialog.',
    icon: <Build />,
    accent: 'warning',
  },
];

const TaskFormDialog: React.FC<TaskFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  customers,
  defaultCustomerId,
  isSubmitting = false,
  initialTask = null,
  assignableUsers,
}) => {
  const { userInfo } = useAuth();
  const theme = useTheme();
  const isEdit = Boolean(initialTask);
  const showAssigneePicker = Boolean(assignableUsers && assignableUsers.length > 0);

  const [selectedCustomer, setSelectedCustomer] = useState<TaskCustomerOption | null>(null);
  const [event, setEvent] = useState<TaskEvent>('follow_up');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState<Dayjs | null>(dayjs().add(1, 'day').startOf('hour'));
  const [error, setError] = useState<string | null>(null);
  const [linkedLeadId, setLinkedLeadId] = useState<string>('');
  const [channels, setChannels] = useState<TaskChannel[]>(['email']);
  const [automationLevel, setAutomationLevel] = useState<TaskAutomationLevel>('partial');
  const [assigneeId, setAssigneeId] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setError(null);

    if (initialTask) {
      setEvent(initialTask.event);
      setTitle(initialTask.title);
      setNotes(initialTask.notes || '');
      setDueDate(initialTask.dueDate ? dayjs(initialTask.dueDate) : null);
      setLinkedLeadId(initialTask.leadId || '');
      setChannels(
        initialTask.preferredChannels && initialTask.preferredChannels.length > 0
          ? initialTask.preferredChannels
          : ['email'],
      );
      setAutomationLevel(initialTask.automationLevel ?? 'partial');
      setAssigneeId(initialTask.assignedToId ? String(initialTask.assignedToId) : '');

      const matchedCustomer =
        customers.find((c) => c.id === initialTask.contact_id) ?? null;
      setSelectedCustomer(matchedCustomer);
      return;
    }

    setEvent('follow_up');
    setTitle('');
    setNotes('');
    setDueDate(dayjs().add(1, 'day').startOf('hour'));
    setLinkedLeadId('');
    setChannels(['email']);
    setAutomationLevel('partial');
    setAssigneeId('');
    if (defaultCustomerId) {
      setSelectedCustomer(customers.find((c) => c.id === defaultCustomerId) || null);
    } else {
      setSelectedCustomer(null);
    }
  }, [open, defaultCustomerId, customers, initialTask, userInfo?.id]);

  // Prefill assignee when the opener is a sales-assignable user. Runs after
  // assignableUsers loads so late data still fills an empty picker.
  useEffect(() => {
    if (!open || initialTask) return;
    const currentUserId = userInfo?.id ? String(userInfo.id) : '';
    if (!currentUserId || !assignableUsers?.length) return;
    if (!assignableUsers.some((user) => user.id === currentUserId)) return;
    setAssigneeId((prev) => prev || currentUserId);
  }, [open, initialTask, userInfo?.id, assignableUsers]);

  const customerLeadOptions = useMemo(() => {
    if (!selectedCustomer) return [] as TaskCustomerLeadSummary[];
    return selectedCustomer.leads;
  }, [selectedCustomer]);

  const handleChannelChange = (_: unknown, value: TaskChannel[]) => {
    if (value.length === 0) return;
    setChannels(value);
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) {
      setError('Pick a contact for this task.');
      return;
    }
    if (!title.trim()) {
      setError('Add a short title.');
      return;
    }
    if (!dueDate || !dueDate.isValid()) {
      setError('Choose a due date.');
      return;
    }
    const requiresChannels = automationLevel !== 'manual';
    if (requiresChannels && channels.length === 0) {
      setError('Pick at least one communication channel.');
      return;
    }
    if (automationLevel === 'full' && !notes.trim()) {
      setError(
        'Add notes — they will be used as the personal message in the auto-sent email/text.',
      );
      return;
    }

    const trimmedAssigneeId = assigneeId.trim();
    // When the assignee picker is hidden, default to the current user (legacy behavior).
    const resolvedAssignedToId =
      trimmedAssigneeId ||
      (!showAssigneePicker && userInfo?.id ? String(userInfo.id) : null);
    let resolvedAssignedToName: string | null = null;
    if (resolvedAssignedToId) {
      const matched = assignableUsers?.find((user) => user.id === resolvedAssignedToId);
      if (matched?.displayName) {
        resolvedAssignedToName = matched.displayName;
      } else if (resolvedAssignedToId === String(userInfo?.id ?? '') && userInfo) {
        const full = `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim();
        resolvedAssignedToName = full || userInfo.email || null;
      } else {
        resolvedAssignedToName = initialTask?.assignedToName ?? null;
      }
    }

    await onSubmit({
      title: title.trim(),
      notes: notes.trim() || null,
      event,
      contact_id: selectedCustomer.id,
      leadId: linkedLeadId || null,
      preferredChannels: requiresChannels ? channels : [],
      automationLevel,
      assignedToId: resolvedAssignedToId,
      assignedToName: resolvedAssignedToName,
      dueDate: dueDate.toDate(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit task' : 'New task'}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Autocomplete
              options={customers}
              value={selectedCustomer}
              onChange={(_, value) => {
                setSelectedCustomer(value);
                setLinkedLeadId('');
              }}
              getOptionLabel={(option) => option.displayName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={
                showAssigneePicker ? 'No matching contacts' : 'No contacts available yet'
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Stack>
                    <span>{option.displayName}</span>
                    {(option.email || option.phone) && (
                      <Box sx={{ color: 'text.secondary', fontSize: 12 }}>
                        {[option.email, option.phone].filter(Boolean).join(' • ')}
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Contact" required placeholder="Search contacts..." />
              )}
            />

            {showAssigneePicker && (
              <Autocomplete
                options={assignableUsers!}
                value={assignableUsers!.find((u) => u.id === assigneeId) ?? null}
                onChange={(_, value) => setAssigneeId(value?.id ?? '')}
                getOptionLabel={(option) => option.displayName}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No reps found"
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack>
                      <span>{option.displayName}</span>
                      {option.email && (
                        <Box sx={{ color: 'text.secondary', fontSize: 12 }}>{option.email}</Box>
                      )}
                    </Stack>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned to"
                    placeholder="Search reps..."
                    helperText="Optional — leave empty for an unassigned task."
                  />
                )}
              />
            )}

            <TextField
              select
              label="Event"
              value={event}
              onChange={(e) => setEvent(e.target.value as TaskEvent)}
            >
              {EVENT_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {TASK_EVENT_LABELS[opt]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="e.g. Wish a happy birthday, send anniversary discount"
            />

            <DatePicker
              label="Due date"
              value={dueDate}
              onChange={(value) => setDueDate(value)}
              slotProps={{ textField: { fullWidth: true } }}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.75 }}
              >
                Automation level
              </Typography>
              <RadioGroup
                value={automationLevel}
                onChange={(e) => setAutomationLevel(e.target.value as TaskAutomationLevel)}
              >
                <Stack spacing={1}>
                  {AUTOMATION_OPTIONS.map((opt) => {
                    const isSelected = automationLevel === opt.value;
                    const accentColor = theme.palette[opt.accent].main;
                    return (
                      <Paper
                        key={opt.value}
                        variant="outlined"
                        onClick={() => setAutomationLevel(opt.value)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderColor: isSelected ? accentColor : 'divider',
                          backgroundColor: isSelected
                            ? alpha(accentColor, 0.08)
                            : 'transparent',
                          transition:
                            'border-color 120ms ease, background-color 120ms ease',
                          '&:hover': {
                            borderColor: accentColor,
                            backgroundColor: alpha(accentColor, 0.06),
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                          <Radio
                            checked={isSelected}
                            value={opt.value}
                            size="small"
                            sx={{ mt: -0.5, color: accentColor, '&.Mui-checked': { color: accentColor } }}
                          />
                          <Box
                            sx={{
                              color: accentColor,
                              display: 'flex',
                              alignItems: 'center',
                              mt: 0.25,
                            }}
                          >
                            {opt.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {opt.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opt.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </RadioGroup>
            </Box>

            {automationLevel !== 'manual' && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Reach out via
                </Typography>
                <ToggleButtonGroup
                  value={channels}
                  onChange={handleChannelChange}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="email">
                    <Email fontSize="small" sx={{ mr: 0.75 }} />
                    Email
                  </ToggleButton>
                  <ToggleButton value="text">
                    <Sms fontSize="small" sx={{ mr: 0.75 }} />
                    Text
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {automationLevel === 'full'
                    ? 'These channels will be sent automatically at 9:00 AM on the due date.'
                    : 'You can still adjust this when you actually complete the task.'}
                </Typography>
              </Box>
            )}

            <TextField
              label={
                automationLevel === 'full'
                  ? 'Personalized message (used in auto-sent email/text)'
                  : 'Notes (optional)'
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={3}
              required={automationLevel === 'full'}
              helperText={
                automationLevel === 'full'
                  ? 'These notes will be inserted as the personal message when the email/text is auto-sent.'
                  : undefined
              }
            />

            {customerLeadOptions.length > 0 && (
              <TextField
                select
                label="Link to a lead (optional)"
                value={linkedLeadId}
                onChange={(e) => setLinkedLeadId(e.target.value)}
                helperText="Pick a specific lead from this contact to attach the task to."
              >
                <MenuItem value="">No specific lead</MenuItem>
                {customerLeadOptions.map((lead) => (
                  <MenuItem key={lead.id} value={lead.id}>
                    {formatLeadOption(lead)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
          {isEdit ? 'Save changes' : 'Create task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialog;
